// A file a custom widget or a Custom board's app hands the board to save to the user's computer (board.download, or a
// Download link in it), since its sealed frame cannot save one itself (David, 1 Nov: a Sketch board's Export did
// nothing). Pictures and plain data only, by their kind, never a page, a script or a program; up to 20 MB; named as the
// app wrote it, with its kind's own ending.

/** The kinds it saves, with the ending each gets. */
const KINDS: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "text/csv": "csv",
  "application/json": "json",
  "text/plain": "txt",
  "text/markdown": "md",
};

/** A name's ending, read as its kind when nothing else says. */
const BY_END: Record<string, string> = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", gif: "image/gif", csv: "text/csv", json: "application/json", txt: "text/plain", md: "text/markdown" };

export const MAX_SAVE = 20 * 1024 * 1024;

const WHAT = "it saves PNG, JPEG, WebP and GIF pictures and CSV, JSON, text and Markdown files";

/** A name's ending as a kind, only one of the list's own (never "constructor" or the like, an object's own names). */
const ending = (end: string): string => (Object.hasOwn(BY_END, end) ? BY_END[end] : "");

/** What the frame sent: board.download's name, type and one of text (or a data: address), buf or blob. */
export interface SaveAsk {
  name?: unknown;
  type?: unknown;
  text?: unknown;
  buf?: unknown;
  blob?: unknown;
}

const kindOf = (t: unknown): string => (typeof t === "string" ? t.split(";")[0].trim().toLowerCase() : "");

/** The file to save, or why it is not saved, in words for the widget's agent. */
export function fileToSave(m: SaveAsk): { name: string; blob: Blob } | { why: string } {
  // a name past 200 characters is cut: nobody names a file so, and a long one only costs time to clean
  const raw = typeof m.name === "string" ? m.name.slice(0, 200) : "";
  const end = /\.([A-Za-z0-9]{1,5})$/.exec(raw)?.[1].toLowerCase() ?? "";
  const text = typeof m.text === "string" ? m.text : null;
  const url = text !== null ? /^data:([^;,]*)((?:;[^;,]*)*?)(;base64)?,/i.exec(text) : null;
  const blob = typeof Blob !== "undefined" && m.blob instanceof Blob ? m.blob : null;
  const buf = m.buf instanceof ArrayBuffer ? m.buf : null;
  if (text === null && !blob && !buf) return { why: "give it the file: text, a data: address, a Blob or an ArrayBuffer" };
  // too big is said before any of it is read or copied
  if ((text?.length ?? 0) > MAX_SAVE * 1.4 || (blob?.size ?? 0) > MAX_SAVE || (buf?.byteLength ?? 0) > MAX_SAVE) return { why: "it saves up to 20 MB" };
  // what kind it is: a data: address says, else the type given, else the Blob's, else the name's ending
  const type = (url ? kindOf(url[1]) : "") || kindOf(m.type) || (blob ? kindOf(blob.type) : "") || ending(end) || "";
  if (!Object.hasOwn(KINDS, type)) return { why: `${type ? `a ${type} file is not saved: ` : "say what kind of file it is (a type, or a name like sketch.png): "}${WHAT}` };
  let bytes: BlobPart;
  if (url && text !== null) {
    const body = text.slice(url[0].length);
    try {
      if (url[3]) {
        const bin = atob(body);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        bytes = out;
      } else bytes = decodeURIComponent(body);
    } catch {
      return { why: "its data: address could not be read" };
    }
  } else bytes = blob ?? buf ?? text ?? "";
  const file = new Blob([bytes], { type: type.startsWith("text/") || type === "application/json" ? `${type};charset=utf-8` : type });
  if (file.size > MAX_SAVE) return { why: `the file is ${Math.ceil(file.size / 1024 / 1024)} MB, and it saves up to 20 MB` };
  // the name as written, less anything a computer cannot take in a file's name, and the ending its kind has
  const base = (ending(end) ? raw.slice(0, -end.length - 1) : raw)
    .replace(/[\\/:*?"<>|\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^[\s.]+|[\s.]+$/g, "")
    .slice(0, 80);
  return { name: `${base || "download"}.${ending(end) === type ? end : KINDS[type]}`, blob: file };
}
